import {useMutation, useQueryClient} from '@tanstack/react-query'
import type {TFunction} from 'i18next'
import {api} from '@/lib/api'
import {publicarExito} from '@/lib/avisos'
import type {components} from '@/lib/api-schema'
import type {TipoDocumento} from '@/lib/documento'
import {detalleDelError, marcarErrorComoComunicado} from '@/lib/errors'

export type AccessGrant = components['schemas']['AccessGrantResponse']

/** SEG-04 (KAI-435): dar acceso, corregir el correo o reenviar la confirmación de un dueño es solo de otro
 * dueño; la API responde 403 a los demás. */
export function tocaElAccesoDe(rolDeLaPersona: string, soyDueno: boolean): boolean {
  return soyDueno || rolDeLaPersona !== 'owner'
}

/** KAI-438: el 409 que pide confirmar una cédula que ya tiene cuenta. Nunca se imprime: es la pregunta que
 * falta, y se reintenta con `confirmar_identidad: true`. */
const CONFIRMAR_IDENTIDAD = 'this document already has a Kairos account: confirm it is the person you mean'

export function pideConfirmarIdentidad(err: unknown): boolean {
  if (detalleDelError(err) !== CONFIRMAR_IDENTIDAD) return false
  marcarErrorComoComunicado(err)
  return true
}

/** La empresa se entera en el momento: el alta quedó sin acceso hasta que soporte verifique a la persona. */
export function avisarSiQuedaEnVerificacion(alta: {verificacion_identidad?: string | null}, t: TFunction): void {
  if (alta.verificacion_identidad !== 'pendiente') return
  publicarExito(t('verificacionIdentidad.agregada.title'), t('verificacionIdentidad.agregada.detalle'), {
    testId: 'empleado-por-verificar',
  })
}

/** Nunca se manda contraseña: la persona la fija desde el enlace de invitación. */
export function useGrantAccess(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (employeeId: string) =>
      (await api.post<AccessGrant>(`/businesses/${businessId}/employees/${employeeId}/user`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['solicitudes', businessId]})
    },
  })
}

export type EmailCorrection = components['schemas']['EmailCorrectionResponse']

/** Solo cuentas que nadie reclamó. Borrar y recrear al empleado no sirve: el alta deduplica
 * por cédula contra la persona, que conserva el correo viejo. */
export function useCorrectEmail(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({employeeId, email}: {employeeId: string; email: string}) =>
      (await api.patch<EmailCorrection>(`/businesses/${businessId}/employees/${employeeId}/user`, {email})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
    },
  })
}

export type ConfirmacionReenviada = components['schemas']['ConfirmacionReenviadaResponse']

/** KAI-139: solo a la dirección ya guardada; no cambia datos, así que no invalida nada. */
export function useResendEmployeeVerification(businessId: string) {
  return useMutation({
    mutationFn: async (employeeId: string) =>
      (
        await api.post<ConfirmacionReenviada>(
          `/businesses/${businessId}/employees/${employeeId}/user/reenviar-confirmacion`
        )
      ).data,
  })
}

export type ResponsableInvite = {
  cedula: string
  tipo_documento: TipoDocumento
  name: string
  last_name: string
  email: string
  confirmar_identidad?: boolean
}

export function useInviteResponsable(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ResponsableInvite) =>
      (await api.post<AccessGrant>(`/businesses/${businessId}/employees/invite-responsable`, payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['business', businessId]})
    },
  })
}
